import React, { useEffect } from 'react';
import { View, FlatList, Text, StyleSheet, TouchableOpacity, RefreshControl, ActivityIndicator, Animated } from 'react-native';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Ionicons } from '@expo/vector-icons';
import { getRooms } from '../../../api/chat';
import { useAuth } from '../../../contexts/AuthContext';
import { useSocket } from '../../../contexts/SocketContext';
import UserAvatar from '../../../components/social/shared/UserAvatar';
import { ChatListSkeleton } from '../../../components/shared/SkeletonLoader';


const ChatListScreen = ({ navigation }) => {
    const { user } = useAuth();
    const { socket, onlineUsers } = useSocket();
    const queryClient = useQueryClient();


    const { data: rooms, isLoading, refetch } = useQuery({
        queryKey: ['chatRooms'],
        queryFn: getRooms,
    });

    useEffect(() => {
        if (socket) {
            const handleNewMessage = () => {
                queryClient.invalidateQueries(['chatRooms']);
            };
            socket.on('new_message', handleNewMessage);
            return () => socket.off('new_message', handleNewMessage);
        }
    }, [socket]);

    const formatTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const getOtherParticipant = (room) => {
        return room.participants?.find(p => p.userId !== user?.id)?.user;
    };

    const renderRoom = ({ item }) => {
        const isGroup = item.type === 'group';
        const otherUser = !isGroup ? getOtherParticipant(item) : null;
        const lastMessage = item.messages?.[0];
        const isOnline = !isGroup && onlineUsers.has(otherUser?.id);

        return (
            <TouchableOpacity
                style={styles.roomItem}
                onPress={() => navigation.navigate('ChatRoom', { roomId: item.id, otherUser })}
            >
                <UserAvatar
                    source={isGroup ? item.avatarUrl : otherUser?.profileImageUrl}
                    name={isGroup ? item.name : otherUser?.name}
                    size={42}
                    showOnline={!isGroup}
                    isOnline={!isGroup && (isOnline || otherUser?.isOnline)}
                    icon={isGroup ? "people" : null}
                />
                <View style={styles.roomInfo}>
                    <View style={styles.roomHeader}>
                        <Text style={styles.roomName}>{isGroup ? item.name : (otherUser?.name || 'Unknown')}</Text>
                        <Text style={styles.timestamp}>{formatTime(item.lastMessageAt)}</Text>
                    </View>
                    <View style={styles.lastMessageRow}>
                        <Text style={styles.lastMessage} numberOfLines={1}>
                            {lastMessage ? (
                                lastMessage.senderId === user?.id ? `You: ${lastMessage.content}` : lastMessage.content
                            ) : 'No messages yet'}
                        </Text>
                        {item.unreadCount > 0 && (
                            <View style={styles.unreadBadge}>
                                <Text style={styles.unreadText}>{item.unreadCount}</Text>
                            </View>
                        )}
                    </View>
                </View>
            </TouchableOpacity>
        );
    };

    if (isLoading) {
        return (
            <View style={styles.container}>
                <ChatListSkeleton count={8} />
            </View>
        );
    }



    return (
        <View style={styles.container}>
            <FlatList
                data={rooms}
                renderItem={renderRoom}

                keyExtractor={item => item.id.toString()}
                ListHeaderComponent={<View style={{ height: 44 }} />}
                contentContainerStyle={styles.listContent}
                refreshControl={
                    <RefreshControl refreshing={isLoading} onRefresh={refetch} />
                }
                ListEmptyComponent={
                    <View style={styles.emptyContainer}>
                        <Text style={styles.emptyText}>No conversations yet</Text>
                    </View>
                }
            />
            <TouchableOpacity
                style={[styles.fab, styles.fabGroup]}
                onPress={() => navigation.navigate('CreateGroup')}
                activeOpacity={0.8}
            >
                <Ionicons name="people" size={24} color="#FFFFFF" />
            </TouchableOpacity>
            <TouchableOpacity
                style={styles.fab}
                onPress={() => navigation.navigate('NewChat')}
                activeOpacity={0.8}
            >
                <Ionicons name="chatbubble-ellipses" size={24} color="#FFFFFF" />
            </TouchableOpacity>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#FFFFFF',
    },
    center: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    listContent: {
        paddingVertical: 5,
    },
    roomItem: {
        flexDirection: 'row',
        paddingHorizontal: 15,
        paddingVertical: 10,
        alignItems: 'center',
    },
    roomInfo: {
        flex: 1,
        marginLeft: 12,
        borderBottomWidth: 1,
        borderBottomColor: '#F3F4F6',
        paddingVertical: 10,
    },
    roomHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 4,
    },
    roomName: {
        fontSize: 15,
        fontWeight: 'bold',
        color: '#111827',
    },
    timestamp: {
        fontSize: 12,
        color: '#6B7280',
    },
    lastMessageRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    lastMessage: {
        fontSize: 13,
        color: '#6B7280',
        flex: 1,
        marginRight: 10,
    },
    unreadBadge: {
        backgroundColor: '#6366F1',
        borderRadius: 10,
        minWidth: 20,
        height: 20,
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: 6,
    },
    unreadText: {
        color: '#FFFFFF',
        fontSize: 11,
        fontWeight: 'bold',
    },
    emptyContainer: {
        padding: 40,
        alignItems: 'center',
    },
    emptyText: {
        color: '#9CA3AF',
        fontSize: 16,
    },
    fab: {
        position: 'absolute',
        bottom: 25,
        right: 25,
        backgroundColor: '#6366F1',
        width: 56,
        height: 56,
        borderRadius: 28,
        alignItems: 'center',
        justifyContent: 'center',
        elevation: 5,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.25,
        shadowRadius: 3.84,
        zIndex: 10,
    },
    fabGroup: {
        bottom: 95,
        backgroundColor: '#8B5CF6',
    }
});

export default ChatListScreen;
